/**
 * Features that can be granted to booking staff, per venue or globally.
 * See doc/user-permissions-plan.md, section 4. Super admins have them all.
 */

export const VENUE_FEATURES = [
  {
    key: "bookings",
    label: "Bookings",
    hint: "Bookings, new booking, calendar, road show",
  },
  { key: "customers", label: "Customers", hint: "Customer list and details" },
  { key: "shows", label: "Shows", hint: "Shows and their content" },
  { key: "studios", label: "Studios", hint: "Studios (rooms) at the venue" },
  { key: "kits", label: "Kits", hint: "Road show kits" },
  { key: "addons", label: "Add-ons", hint: "Add-ons for bookings" },
  {
    key: "shop_items",
    label: "Shop items",
    hint: "Shop items sold at the venue",
  },
  { key: "holidays", label: "Holidays", hint: "Closed days and studio blocks" },
  { key: "faqs", label: "FAQs", hint: "Venue and show FAQs" },
  {
    key: "pricing",
    label: "Pricing",
    hint: "Show prices and time slot pricing",
  },
  {
    key: "rule_sets",
    label: "Rules",
    hint: "Booking change and cancellation rules for shows",
  },
  {
    key: "price_modifiers",
    label: "Price modifiers",
    hint: "Time-based price modifiers",
  },
  { key: "invoices", label: "Invoices", hint: "Invoices and invoice tracking" },
  {
    key: "accounting_settings",
    label: "Accounting settings",
    hint: "Accounting connection, article mappings",
  },
  { key: "inbox", label: "Inbox", hint: "Read and answer the venue's email" },
  { key: "reviews", label: "Reviews", hint: "Reviews and ratings" },
  {
    key: "review_questions",
    label: "Review questions",
    hint: "The questions in the venue's review form",
  },
  {
    key: "users",
    label: "User management",
    hint: "Add and edit staff for this venue, within their own access",
  },
  {
    key: "venue_settings",
    label: "Venue settings",
    hint: "Venue details",
  },
  {
    key: "email_templates",
    label: "Show email templates",
    hint: "Booking emails for shows and road shows",
  },
  { key: "partners", label: "Partners", hint: "The venue's partners" },
] as const;

/**
 * Booking staff creating venues (`venues_admin`) is hidden for now: no
 * switch to grant it, no Create venue button in the booking portal. Set to
 * true to bring it back; the server check stays in place either way.
 */
export const CREATE_VENUES_FOR_STAFF = false;

export const GLOBAL_FEATURES = [
  { key: "venues_admin", label: "Create venues", hint: "Add new venues" },
  {
    key: "ai_settings",
    label: "AI settings",
    hint: "Assistant prompts, reply templates, knowledge base",
  },
  {
    key: "inbox_global",
    label: "Shared mailboxes",
    hint: "Mailboxes not tied to a venue; connecting Gmail",
  },
] as const;

export type VenueFeature = (typeof VENUE_FEATURES)[number]["key"];
export type GlobalFeature = (typeof GLOBAL_FEATURES)[number]["key"];

/**
 * What a booking staff member gets at a venue unless told otherwise: what
 * booking staff can do today. Inbox, reviews, review questions, user
 * management, accounting settings and partners are granted person by person.
 */
export const DEFAULT_VENUE_FEATURES: VenueFeature[] = [
  "bookings",
  "customers",
  "shows",
  "studios",
  "kits",
  "addons",
  "shop_items",
  "holidays",
  "faqs",
  "pricing",
  "rule_sets",
  "price_modifiers",
  "invoices",
  "venue_settings",
  "email_templates",
];
